import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import axios from 'axios';
import { toast } from 'sonner';
import {
  TrendUp, Users, Buildings, Robot, SignOut, CaretDown, Article, Palette, Ticket, EnvelopeSimple, PencilLine,
  ChartBar, FileText, Storefront, DownloadSimple, MagnifyingGlass, Handshake,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import BlogAdminPanel from '@/components/BlogAdminPanel';
import BrandingPanel from '@/components/BrandingPanel';
import CouponsAdminPanel from '@/components/CouponsAdminPanel';
import SiteContentAdminPanel from '@/components/SiteContentAdminPanel';
import Logo from '@/components/Logo';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ label, value, delta, accent = '#0F1B36' }) => (
  <div className="bg-white border border-[#E4E4E7] p-5">
    <p className="text-xs uppercase tracking-widest font-semibold text-[#71717A] mb-2">{label}</p>
    <p className="text-3xl font-black text-[#09090B] mb-1" style={{ color: accent }}>{value}</p>
    {delta && <p className="text-xs text-[#52525B]">{delta}</p>}
  </div>
);

const ProgressRow = ({ label, value, total, color = '#0F1B36' }) => {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-[#27272A] font-medium">{label}</span>
        <span className="text-[#52525B]">{value} <span className="text-[#71717A]">({pct}%)</span></span>
      </div>
      <div className="h-2 bg-[#F4F4F5]">
        <div className="h-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
    </div>
  );
};

const AdminPage = () => {
  const navigate = useNavigate();
  const { user, logout, getAuthHeaders } = useAuth();
  const { t } = useLanguage();
  const [users, setUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [toolLeads, setToolLeads] = useState([]);
  const [consultingLeads, setConsultingLeads] = useState([]);
  const [consultingLoaded, setConsultingLoaded] = useState(false);
  const [consultingLoading, setConsultingLoading] = useState(false);
  const [toolRuns, setToolRuns] = useState({ total: 0, by_tool: {}, results: [] });
  const [toolRunsLoaded, setToolRunsLoaded] = useState(false);
  const [toolRunsLoading, setToolRunsLoading] = useState(false);
  const [overview, setOverview] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [companiesLoaded, setCompaniesLoaded] = useState(false);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [assessments, setAssessments] = useState([]);
  const [assessmentsLoaded, setAssessmentsLoaded] = useState(false);
  const [assessmentsLoading, setAssessmentsLoading] = useState(false);
  const [sectors, setSectors] = useState([]);
  const [companySearch, setCompanySearch] = useState('');
  const [loading, setLoading] = useState(true);

  // Fast initial load: only /overview + /users + /organizations + /prompts + /tool-leads + /sectors.
  // The heavy /companies (~7s) and /assessments (~5s) are lazy-loaded when their tab is clicked.
  const fetchAdminData = useCallback(async () => {
    try {
      const [usersRes, orgsRes, promptsRes, leadsRes, ovRes, secRes] = await Promise.all([
        axios.get(`${API_URL}/admin/users`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/admin/organizations`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/admin/prompts`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/admin/tool-leads`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/admin/overview`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/admin/sectors`, { headers: getAuthHeaders() }),
      ]);
      return {
        users: usersRes.data,
        organizations: orgsRes.data,
        prompts: promptsRes.data,
        toolLeads: leadsRes.data,
        overview: ovRes.data,
        sectors: secRes.data?.breakdown || [],
      };
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/dashboard');
      } else { toast.error('Failed to load admin data'); }
      return null;
    }
  }, [getAuthHeaders, navigate]);

  const loadCompanies = useCallback(async () => {
    if (companiesLoaded || companiesLoading) return;
    setCompaniesLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/companies?limit=500`, { headers: getAuthHeaders() });
      setCompanies(res.data?.results || []);
      setCompaniesLoaded(true);
    } catch {
      toast.error('Firma listesi yüklenemedi');
    } finally {
      setCompaniesLoading(false);
    }
  }, [companiesLoaded, companiesLoading, getAuthHeaders]);

  const loadToolRuns = useCallback(async () => {
    if (toolRunsLoaded || toolRunsLoading) return;
    setToolRunsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/tool-runs?limit=500`, { headers: getAuthHeaders() });
      setToolRuns(res.data || { total: 0, by_tool: {}, results: [] });
      setToolRunsLoaded(true);
    } catch {
      toast.error('Araç kullanımları yüklenemedi');
    } finally {
      setToolRunsLoading(false);
    }
  }, [toolRunsLoaded, toolRunsLoading, getAuthHeaders]);

  const loadConsultingLeads = useCallback(async () => {
    if (consultingLoaded || consultingLoading) return;
    setConsultingLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/consulting-leads?limit=500`, { headers: getAuthHeaders() });
      setConsultingLeads(res.data || []);
      setConsultingLoaded(true);
    } catch {
      toast.error('Danışmanlık talepleri yüklenemedi');
    } finally {
      setConsultingLoading(false);
    }
  }, [consultingLoaded, consultingLoading, getAuthHeaders]);

  const updateConsultingLead = async (id, patch) => {
    try {
      await axios.patch(`${API_URL}/admin/consulting-leads/${id}`, patch, { headers: getAuthHeaders() });
      setConsultingLeads((leads) => leads.map((l) => l.id === id ? { ...l, ...patch } : l));
      toast.success('Güncellendi');
    } catch {
      toast.error('Güncelleme başarısız');
    }
  };

  const loadAssessments = useCallback(async () => {
    if (assessmentsLoaded || assessmentsLoading) return;
    setAssessmentsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/assessments?limit=500`, { headers: getAuthHeaders() });
      setAssessments(res.data?.results || []);
      setAssessmentsLoaded(true);
    } catch {
      toast.error('Değerlendirme listesi yüklenemedi');
    } finally {
      setAssessmentsLoading(false);
    }
  }, [assessmentsLoaded, assessmentsLoading, getAuthHeaders]);

  useEffect(() => {
    let mounted = true;
    fetchAdminData().then((data) => {
      if (!mounted) return;
      if (data) {
        setUsers(data.users);
        setOrganizations(data.organizations);
        setPrompts(data.prompts);
        setToolLeads(data.toolLeads);
        setOverview(data.overview);
        setSectors(data.sectors);
      }
      setLoading(false);
    }).finally(() => {
      // Always release the spinner even when an earlier effect was unmounted —
      // prevents a stuck "Yükleniyor…" if React re-runs the effect mid-fetch.
      if (mounted) setLoading(false);
    });
    return () => { mounted = false; };
  }, [fetchAdminData]);

  const handleLogout = () => { logout(); navigate('/'); };

  const exportLeadsCSV = () => {
    const header = 'email,tool_slug,consent_marketing,created_at';
    const rows = toolLeads.map((lead) => `${lead.email},${lead.tool_slug},${lead.consent_marketing},${lead.created_at}`);
    const csv = [header, ...rows].join('\n');
    const dataUrl = `data:text/csv;charset=utf-8,${encodeURIComponent(csv)}`;
    window.open(dataUrl, '_blank');
  };

  const exportCompaniesCSV = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/export/companies.csv`, {
        headers: getAuthHeaders(),
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `growthriq_companies_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error('CSV indirilemedi');
    }
  };

  const filteredCompanies = companies.filter((c) => {
    if (!companySearch.trim()) return true;
    const s = companySearch.toLowerCase();
    return [c.name, c.industry, c.country, c.owner?.email, c.owner?.name]
      .filter(Boolean).some((v) => String(v).toLowerCase().includes(s));
  });

  return (
    <div className="min-h-screen bg-[#F4F4F5]">
      <header className="bg-white border-b border-[#E4E4E7]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Link to="/dashboard" className="flex items-center gap-2">
              <Logo variant="light" size="md" />
            </Link>
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/dashboard" className="text-[#52525B] hover:text-[#09090B] transition-colors">{t('nav.dashboard')}</Link>
              <Link to="/companies" className="text-[#52525B] hover:text-[#09090B] transition-colors">{t('nav.companies')}</Link>
              <Link to="/admin" className="text-[#09090B] font-medium">{t('nav.admin')}</Link>
            </nav>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-[#E60000] flex items-center justify-center">
                    <span className="text-white text-sm font-bold">A</span>
                  </div>
                  <span className="text-[#09090B]">{t('nav.admin')}</span>
                  <CaretDown size={16} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleLogout} className="text-[#E60000]">
                  <SignOut size={16} className="mr-2" />{t('nav.signout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="heading-2 text-[#09090B] mb-2" data-testid="admin-title">{t('admin.title')}</h1>
          <p className="text-[#52525B]">{t('admin.subtitle')}</p>
        </div>

        {loading ? (
          <div className="text-center py-12 text-[#52525B]">{t('common.loading')}</div>
        ) : (
          <Tabs defaultValue="overview" className="space-y-6" onValueChange={(v) => {
            if (v === 'companies') loadCompanies();
            if (v === 'assessments') loadAssessments();
            if (v === 'tool-runs') loadToolRuns();
            if (v === 'consulting') loadConsultingLeads();
          }}>
            <TabsList className="bg-white border border-[#E4E4E7] p-1 rounded-none flex-wrap h-auto">
              <TabsTrigger value="overview" className="rounded-none data-[state=active]:bg-[#0F1B36] data-[state=active]:text-white" data-testid="admin-tab-overview">
                <ChartBar size={16} className="mr-2" />Genel Bakış
              </TabsTrigger>
              <TabsTrigger value="companies" className="rounded-none data-[state=active]:bg-[#0F1B36] data-[state=active]:text-white" data-testid="admin-tab-companies">
                <Storefront size={16} className="mr-2" />Firmalar {overview?.counts?.companies?.total ? `(${overview.counts.companies.total})` : ''}
              </TabsTrigger>
              <TabsTrigger value="assessments" className="rounded-none data-[state=active]:bg-[#0F1B36] data-[state=active]:text-white" data-testid="admin-tab-assessments">
                <FileText size={16} className="mr-2" />Değerlendirmeler {overview?.counts?.assessments?.total ? `(${overview.counts.assessments.total})` : ''}
              </TabsTrigger>
              <TabsTrigger value="users" className="rounded-none data-[state=active]:bg-[#0F1B36] data-[state=active]:text-white">
                <Users size={16} className="mr-2" />{t('admin.users')} ({users.length})
              </TabsTrigger>
              <TabsTrigger value="organizations" className="rounded-none data-[state=active]:bg-[#0F1B36] data-[state=active]:text-white">
                <Buildings size={16} className="mr-2" />{t('admin.organizations')} ({organizations.length})
              </TabsTrigger>
              <TabsTrigger value="prompts" className="rounded-none data-[state=active]:bg-[#0F1B36] data-[state=active]:text-white">
                <Robot size={16} className="mr-2" />{t('admin.prompts')} ({prompts.length})
              </TabsTrigger>
              <TabsTrigger value="blog" className="rounded-none data-[state=active]:bg-[#0F1B36] data-[state=active]:text-white" data-testid="admin-tab-blog">
                <Article size={16} className="mr-2" />{t('admin.blog')}
              </TabsTrigger>
              <TabsTrigger value="branding" className="rounded-none data-[state=active]:bg-[#0F1B36] data-[state=active]:text-white" data-testid="admin-tab-branding">
                <Palette size={16} className="mr-2" />{t('branding.tab')}
              </TabsTrigger>
              <TabsTrigger value="coupons" className="rounded-none data-[state=active]:bg-[#0F1B36] data-[state=active]:text-white" data-testid="admin-tab-coupons">
                <Ticket size={16} className="mr-2" />{t('admin.coupons')}
              </TabsTrigger>
              <TabsTrigger value="leads" className="rounded-none data-[state=active]:bg-[#0F1B36] data-[state=active]:text-white" data-testid="admin-tab-leads">
                <EnvelopeSimple size={16} className="mr-2" />Lead&apos;ler ({toolLeads.length})
              </TabsTrigger>
              <TabsTrigger value="consulting" className="rounded-none data-[state=active]:bg-[#0F1B36] data-[state=active]:text-white" data-testid="admin-tab-consulting">
                <Handshake size={16} className="mr-2" />Danışmanlık {consultingLeads.length ? `(${consultingLeads.length})` : ''}
              </TabsTrigger>
              <TabsTrigger value="tool-runs" className="rounded-none data-[state=active]:bg-[#0F1B36] data-[state=active]:text-white" data-testid="admin-tab-tool-runs">
                <Robot size={16} className="mr-2" />Araç Kullanımı {overview?.counts?.tool_runs?.total ? `(${overview.counts.tool_runs.total})` : ''}
              </TabsTrigger>
              <TabsTrigger value="content" className="rounded-none data-[state=active]:bg-[#0F1B36] data-[state=active]:text-white" data-testid="admin-tab-content">
                <PencilLine size={16} className="mr-2" />Site İçerikleri
              </TabsTrigger>
            </TabsList>

            {/* OVERVIEW */}
            <TabsContent value="overview" className="space-y-6">
              {overview && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <StatCard label="Toplam Kullanıcı" value={overview.counts.users.total} delta={`+${overview.counts.users.last_24h} son 24s`} accent="#0F1B36" />
                    <StatCard label="Toplam Firma" value={overview.counts.companies.total} delta={`+${overview.counts.companies.last_24h} son 24s`} accent="#3CD9B8" />
                    <StatCard label="Tamamlanan Rapor" value={overview.counts.reports.total} delta={`+${overview.counts.reports.last_24h} son 24s`} accent="#16A34A" />
                    <StatCard label="Ortalama Skor" value={`${overview.scores.average_overall}`} delta={`${overview.scores.completed_count} tamamlanmış`} accent="#D97706" />
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-white border border-[#E4E4E7] p-6">
                      <h3 className="heading-4 text-[#09090B] mb-4">Değerlendirme Durumu</h3>
                      <div className="space-y-3">
                        <ProgressRow label="Tamamlandı" value={overview.counts.assessments.completed} total={overview.counts.assessments.total} color="#16A34A" />
                        <ProgressRow label="Devam Ediyor" value={overview.counts.assessments.in_progress} total={overview.counts.assessments.total} color="#D97706" />
                      </div>
                      <p className="text-sm text-[#52525B] mt-4">
                        Tamamlanma oranı: <strong className="text-[#0F1B36]">{overview.counts.assessments.completion_rate}%</strong>
                      </p>
                    </div>
                    <div className="bg-white border border-[#E4E4E7] p-6">
                      <h3 className="heading-4 text-[#09090B] mb-4">Skor Dağılımı</h3>
                      <div className="space-y-2">
                        {['0-29', '30-49', '50-69', '70-84', '85-100'].map((b) => (
                          <ProgressRow key={b} label={b} value={overview.scores.distribution[b] || 0} total={overview.scores.completed_count || 1} color="#0F1B36" />
                        ))}
                      </div>
                    </div>
                    <div className="bg-white border border-[#E4E4E7] p-6">
                      <h3 className="heading-4 text-[#09090B] mb-4">Sektör Dağılımı</h3>
                      {sectors.length === 0 ? <p className="text-sm text-[#52525B]">Henüz veri yok</p> : (
                        <div className="space-y-2 max-h-72 overflow-y-auto">
                          {sectors.map((s) => (
                            <div key={s.sector} className="flex items-center justify-between text-sm">
                              <span className="text-[#27272A]">{s.sector}</span>
                              <div className="flex items-center gap-2">
                                <div className="w-24 h-1.5 bg-[#F4F4F5] rounded-none">
                                  <div className="h-full bg-[#3CD9B8]" style={{ width: `${s.percentage}%` }} />
                                </div>
                                <span className="text-[#52525B] w-14 text-right">{s.count} <span className="text-xs text-[#71717A]">({s.percentage}%)</span></span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="bg-white border border-[#E4E4E7] p-6">
                      <h3 className="heading-4 text-[#09090B] mb-4">Diğer Ölçümler</h3>
                      <ul className="text-sm text-[#27272A] space-y-2">
                        <li>Organizasyon: <strong>{overview.counts.organizations.total}</strong> (+{overview.counts.organizations.last_24h} son 24s)</li>
                        <li>Aktif değerlendirme: <strong>{overview.counts.assessments.in_progress}</strong></li>
                        <li>Ücretsiz araç kullanımı (auth): <strong>{overview.counts.tool_runs.total}</strong> (+{overview.counts.tool_runs.last_24h} son 24s)</li>
                        <li>Lead capture: <strong>{overview.counts.tool_leads.total}</strong></li>
                        <li className="pt-2 border-t border-[#F4F4F5] text-xs text-[#71717A]">
                          Son güncelleme: {new Date(overview.generated_at).toLocaleString('tr-TR')}
                        </li>
                      </ul>
                    </div>
                  </div>
                </>
              )}
            </TabsContent>

            {/* COMPANIES */}
            <TabsContent value="companies" className="space-y-4">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="relative flex-1 max-w-md">
                  <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" />
                  <input
                    type="text"
                    value={companySearch}
                    onChange={(e) => setCompanySearch(e.target.value)}
                    placeholder="Firma, sektör, sahip email ara…"
                    className="w-full pl-10 pr-4 py-2 bg-white border border-[#E4E4E7] focus:border-[#0F1B36] outline-none text-sm"
                    data-testid="admin-companies-search"
                  />
                </div>
                <Button onClick={exportCompaniesCSV} className="rounded-none bg-[#0F1B36] hover:bg-[#00227A] text-white" data-testid="admin-export-companies">
                  <DownloadSimple size={16} className="mr-2" />CSV İndir
                </Button>
              </div>
              <div className="bg-white border border-[#E4E4E7] overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">Firma</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">Sektör</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">Sahip</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">Değ.</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">Tamamlanan</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">En İyi Skor</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">Kayıt</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {companiesLoading && (
                      <TableRow><TableCell colSpan={7} className="text-center py-8 text-[#52525B]" data-testid="admin-companies-loading">Firmalar yükleniyor…</TableCell></TableRow>
                    )}
                    {!companiesLoading && filteredCompanies.map((c) => (
                      <TableRow key={c.id} data-testid={`admin-company-row-${c.id}`}>
                        <TableCell className="font-medium text-[#09090B]">{c.name}<div className="text-xs text-[#71717A]">{c.country || '—'} · {c.business_model || '—'}</div></TableCell>
                        <TableCell className="text-sm text-[#27272A]">{c.industry || '—'}</TableCell>
                        <TableCell>
                          <div className="text-sm text-[#09090B]">{c.owner?.name || '—'}</div>
                          <div className="text-xs text-[#71717A]">{c.owner?.email || ''}</div>
                        </TableCell>
                        <TableCell className="text-sm text-[#27272A]">{c.metrics.assessments_total}</TableCell>
                        <TableCell><span className={`text-xs font-bold px-2 py-1 ${c.metrics.assessments_completed > 0 ? 'bg-[#16A34A]/10 text-[#16A34A]' : 'bg-[#F4F4F5] text-[#71717A]'}`}>{c.metrics.assessments_completed}</span></TableCell>
                        <TableCell className="text-sm font-semibold text-[#0F1B36]">{c.metrics.best_overall_score ? Number(c.metrics.best_overall_score).toFixed(0) : '—'}</TableCell>
                        <TableCell className="text-xs text-[#71717A]">{c.created_at ? new Date(c.created_at).toLocaleDateString('tr-TR') : '—'}</TableCell>
                      </TableRow>
                    ))}
                    {!companiesLoading && filteredCompanies.length === 0 && (
                      <TableRow><TableCell colSpan={7} className="text-center py-8 text-[#71717A]">Henüz kayıtlı firma yok</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            {/* ASSESSMENTS */}
            <TabsContent value="assessments">
              <div className="bg-white border border-[#E4E4E7] overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">ID</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">Firma</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">Sektör</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">Sahip</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">Tip / Seviye</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">Durum</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">İlerleme</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">Skor</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">Rapor</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">Güncelleme</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {assessmentsLoading && (
                      <TableRow><TableCell colSpan={10} className="text-center py-8 text-[#52525B]" data-testid="admin-assessments-loading">Değerlendirmeler yükleniyor…</TableCell></TableRow>
                    )}
                    {!assessmentsLoading && assessments.map((a) => (
                      <TableRow key={a.id} data-testid={`admin-assessment-row-${a.id}`}>
                        <TableCell className="text-xs text-[#71717A]">{a.id}</TableCell>
                        <TableCell className="font-medium text-[#09090B]">{a.company_name || '—'}</TableCell>
                        <TableCell className="text-sm text-[#27272A]">{a.industry || '—'}</TableCell>
                        <TableCell className="text-xs text-[#52525B]">{a.owner_email || '—'}</TableCell>
                        <TableCell className="text-xs uppercase tracking-wider text-[#52525B]">{a.type} / {a.level}</TableCell>
                        <TableCell>
                          <span className={`text-xs font-bold px-2 py-1 ${
                            a.status === 'completed' ? 'bg-[#16A34A]/10 text-[#16A34A]' :
                            a.status === 'in_progress' ? 'bg-[#D97706]/10 text-[#D97706]' :
                            'bg-[#F4F4F5] text-[#71717A]'
                          }`}>{a.status?.toUpperCase()}</span>
                        </TableCell>
                        <TableCell className="text-xs text-[#52525B]">{a.current_index}/{a.total_questions}</TableCell>
                        <TableCell className="text-sm font-semibold text-[#0F1B36]">{a.overall_score ? Number(a.overall_score).toFixed(0) : '—'}</TableCell>
                        <TableCell>
                          {a.has_report ? (
                            <Link to={`/report/${a.id}`} className="text-xs text-[#0F1B36] underline hover:text-[#00227A]" data-testid={`admin-view-report-${a.id}`}>Görüntüle</Link>
                          ) : <span className="text-xs text-[#71717A]">Yok</span>}
                        </TableCell>
                        <TableCell className="text-xs text-[#71717A]">{a.updated_at ? new Date(a.updated_at).toLocaleString('tr-TR') : '—'}</TableCell>
                      </TableRow>
                    ))}
                    {!assessmentsLoading && assessments.length === 0 && (
                      <TableRow><TableCell colSpan={10} className="text-center py-8 text-[#71717A]">Henüz değerlendirme yok</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            <TabsContent value="users">
              <div className="bg-white border border-[#E4E4E7]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">{t('admin.table.id')}</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">{t('admin.table.email')}</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">{t('admin.table.name')}</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">{t('admin.table.role')}</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">{t('admin.table.organization')}</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">{t('admin.table.admin')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((u) => (
                      <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                        <TableCell className="font-mono text-sm">{u.id}</TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell>{u.first_name} {u.last_name}</TableCell>
                        <TableCell><span className="text-xs uppercase bg-[#F4F4F5] px-2 py-1">{u.role}</span></TableCell>
                        <TableCell>{u.organization_name || '-'}</TableCell>
                        <TableCell>
                          {u.is_admin
                            ? <span className="text-xs bg-[#E60000]/10 text-[#E60000] px-2 py-1">{t('common.yes')}</span>
                            : <span className="text-xs text-[#52525B]">{t('common.no')}</span>
                          }
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            <TabsContent value="organizations">
              <div className="bg-white border border-[#E4E4E7]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">{t('admin.table.id')}</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">{t('admin.table.name')}</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">{t('admin.table.plan')}</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">{t('admin.table.users')}</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">{t('admin.table.companies')}</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">{t('admin.table.created')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {organizations.map((org) => (
                      <TableRow key={org.id} data-testid={`org-row-${org.id}`}>
                        <TableCell className="font-mono text-sm">{org.id}</TableCell>
                        <TableCell className="font-medium">{org.name}</TableCell>
                        <TableCell>
                          <span className={`text-xs uppercase px-2 py-1 ${
                            org.plan_type === 'advanced' ? 'bg-[#0F1B36]/10 text-[#0F1B36]'
                            : org.plan_type === 'basic' ? 'bg-[#16A34A]/10 text-[#16A34A]'
                            : 'bg-[#F4F4F5] text-[#52525B]'
                          }`}>{org.plan_type}</span>
                        </TableCell>
                        <TableCell>{org.user_count}</TableCell>
                        <TableCell>{org.company_count}</TableCell>
                        <TableCell className="text-sm text-[#52525B]">{new Date(org.created_at).toLocaleDateString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            <TabsContent value="prompts">
              <div className="bg-white border border-[#E4E4E7]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">{t('admin.table.id')}</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">{t('admin.table.name')}</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">{t('admin.table.type')}</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">{t('admin.table.preview')}</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">{t('admin.table.active')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {prompts.map((prompt) => (
                      <TableRow key={prompt.id} data-testid={`prompt-row-${prompt.id}`}>
                        <TableCell className="font-mono text-sm">{prompt.id}</TableCell>
                        <TableCell className="font-medium">{prompt.name}</TableCell>
                        <TableCell><span className="text-xs uppercase bg-[#0F1B36]/10 text-[#0F1B36] px-2 py-1">{prompt.prompt_type}</span></TableCell>
                        <TableCell className="max-w-md"><p className="text-sm text-[#52525B] truncate">{prompt.content}</p></TableCell>
                        <TableCell>
                          {prompt.is_active
                            ? <span className="text-xs bg-[#16A34A]/10 text-[#16A34A] px-2 py-1">{t('admin.table.active')}</span>
                            : <span className="text-xs bg-[#E60000]/10 text-[#E60000] px-2 py-1">{t('admin.table.inactive')}</span>
                          }
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            <TabsContent value="blog">
              <BlogAdminPanel />
            </TabsContent>

            <TabsContent value="branding">
              <BrandingPanel />
            </TabsContent>

            <TabsContent value="coupons">
              <CouponsAdminPanel />
            </TabsContent>

            <TabsContent value="leads">
              <div className="bg-white border border-[#E4E4E7]">
                <div className="px-4 py-3 border-b border-[#E4E4E7] flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-[#09090B]">Ücretsiz Araç Lead&apos;leri</h3>
                    <p className="text-xs text-[#71717A] mt-0.5">Sisteme kayıt olmadan ücretsiz aracı kullanan potansiyel müşteriler ve test sonuçları.</p>
                  </div>
                  <button
                    onClick={exportLeadsCSV}
                    data-testid="leads-csv-export"
                    className="text-xs px-3 py-1.5 bg-[#0F1B36] text-white rounded hover:opacity-90"
                  >CSV indir</button>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">E-posta</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">Araç</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">Sonuç Özeti</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">İzin</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">Tarih</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {toolLeads.length === 0 && (
                      <TableRow><TableCell colSpan={5} className="text-center text-[#52525B] py-8">Henüz lead yok.</TableCell></TableRow>
                    )}
                    {toolLeads.map((lead) => {
                      const s = lead.summary || {};
                      const skor = s.genel_skor ?? s.score;
                      const targetUrl = s.url || s.target_url || s.domain;
                      const sirket = s.sirket_adi || s.product_name;
                      return (
                      <TableRow key={lead.id} data-testid={`lead-row-${lead.id}`}>
                        <TableCell className="font-medium">
                          <a href={`mailto:${lead.email}`} className="text-[#0F1B36] hover:underline">{lead.email}</a>
                        </TableCell>
                        <TableCell><span className="text-xs uppercase bg-[#3CD9B8]/10 text-[#0F1B36] px-2 py-1">{lead.tool_slug}</span></TableCell>
                        <TableCell className="text-sm">
                          {sirket && <div className="font-medium text-[#0F1B36]">{sirket}</div>}
                          {targetUrl && <div className="text-xs text-[#52525B] truncate max-w-[200px]" title={targetUrl}>{targetUrl}</div>}
                          {skor !== undefined && (
                            <div className="text-xs mt-1">
                              Skor: <span className="font-bold" style={{color: skor >= 60 ? '#16A34A' : skor >= 40 ? '#CA8A04' : '#E60000'}}>{skor}</span>
                              {s.readiness_level && <span className="text-[#71717A] ml-2">({s.readiness_level})</span>}
                            </div>
                          )}
                          {!skor && !sirket && !targetUrl && <span className="text-xs text-[#A1A1AA]">-</span>}
                        </TableCell>
                        <TableCell>{lead.consent_marketing ? <span className="text-xs bg-[#16A34A]/10 text-[#16A34A] px-2 py-1">✓ Evet</span> : <span className="text-xs bg-[#F4F4F5] text-[#71717A] px-2 py-1">Hayır</span>}</TableCell>
                        <TableCell className="text-sm text-[#52525B]">{lead.created_at ? new Date(lead.created_at).toLocaleString('tr-TR') : '-'}</TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            <TabsContent value="consulting">
              <div className="bg-white border border-[#E4E4E7]">
                <div className="px-4 py-3 border-b border-[#E4E4E7] flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-[#09090B]">E-İhracat Danışmanlık Talepleri</h3>
                    <p className="text-xs text-[#71717A] mt-0.5">
                      /e-ihracat-danismanlik sayfasından form dolduran potansiyel müşteriler. Reklamlarınızdan gelen leadler burada.
                    </p>
                  </div>
                </div>
                {consultingLoading ? (
                  <div className="text-center py-12 text-[#52525B]">Yükleniyor…</div>
                ) : consultingLeads.length === 0 ? (
                  <div className="text-center py-12 text-[#52525B]">Henüz danışmanlık talebi yok.</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">Kişi</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">Firma / Ciro</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">Hedef Pazarlar</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">Kaynak</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">Durum</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">Tarih</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {consultingLeads.map((l) => (
                        <TableRow key={l.id} data-testid={`consulting-row-${l.id}`}>
                          <TableCell className="font-medium">
                            <div className="text-sm text-[#0F1B36] font-bold">{l.full_name}</div>
                            <a href={`mailto:${l.email}`} className="text-xs text-[#52525B] hover:underline block">{l.email}</a>
                            {l.phone && <a href={`tel:${l.phone}`} className="text-xs text-[#3CD9B8] hover:underline block">{l.phone}</a>}
                          </TableCell>
                          <TableCell className="text-sm">
                            {l.company_name && <div className="font-medium text-[#0F1B36]">{l.company_name}</div>}
                            {l.website && <a href={l.website.startsWith('http') ? l.website : `https://${l.website}`} target="_blank" rel="noreferrer" className="text-xs text-[#3CD9B8] hover:underline truncate max-w-[200px] block">{l.website}</a>}
                            {l.sector && <div className="text-xs text-[#71717A]">{l.sector}</div>}
                            {l.annual_revenue && <div className="text-xs mt-1"><span className="bg-[#3CD9B8]/10 text-[#0F1B36] px-2 py-0.5 font-semibold">{l.annual_revenue}</span></div>}
                          </TableCell>
                          <TableCell className="text-sm">
                            {(l.target_markets || []).slice(0, 4).map((m) => (
                              <span key={m} className="inline-block text-[10px] bg-[#0F1B36] text-white px-1.5 py-0.5 mr-1 mb-1 rounded">{m}</span>
                            ))}
                            {(l.target_markets || []).length > 4 && <span className="text-xs text-[#71717A]">+{l.target_markets.length - 4}</span>}
                          </TableCell>
                          <TableCell className="text-xs">
                            <div className="font-semibold text-[#0F1B36]">{l.source || 'organic'}</div>
                            {l.utm_campaign && <div className="text-[10px] text-[#71717A]">{l.utm_campaign}</div>}
                          </TableCell>
                          <TableCell>
                            <select
                              value={l.status || 'new'}
                              onChange={(e) => updateConsultingLead(l.id, { status: e.target.value })}
                              className="text-xs border border-[#E4E4E7] rounded px-2 py-1 bg-white"
                              data-testid={`consulting-status-${l.id}`}
                            >
                              <option value="new">Yeni</option>
                              <option value="contacted">Arandı</option>
                              <option value="qualified">Uygun</option>
                              <option value="won">Anlaşıldı</option>
                              <option value="lost">Kayıp</option>
                            </select>
                          </TableCell>
                          <TableCell className="text-xs text-[#52525B]">{l.created_at ? new Date(l.created_at).toLocaleString('tr-TR') : '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            </TabsContent>

            <TabsContent value="tool-runs">
              <div className="bg-white border border-[#E4E4E7]">
                <div className="px-4 py-3 border-b border-[#E4E4E7] flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-[#09090B]">Ücretsiz Araç Kullanımları</h3>
                    <p className="text-xs text-[#71717A] mt-0.5">
                      Giriş yapmış kullanıcıların çalıştırdığı GEO Checkup, Rakip Radar, Rapor Analitik ve Fiyat Motoru analizleri.
                    </p>
                  </div>
                  {Object.keys(toolRuns.by_tool || {}).length > 0 && (
                    <div className="flex gap-2">
                      {Object.entries(toolRuns.by_tool).map(([slug, cnt]) => (
                        <span key={slug} className="text-xs bg-[#3CD9B8]/10 text-[#0F1B36] px-2 py-1">
                          {slug}: <strong>{cnt}</strong>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                {toolRunsLoading ? (
                  <div className="text-center py-12 text-[#52525B]">Yükleniyor…</div>
                ) : (toolRuns.results || []).length === 0 ? (
                  <div className="text-center py-12 px-6">
                    <div className="text-[#52525B] mb-3">Henüz kayıtlı araç kullanımı yok.</div>
                    <p className="text-xs text-[#71717A] max-w-lg mx-auto leading-relaxed">
                      Araçlar (GEO Checkup, Rakip Radar vs.) hem <strong>anonim</strong> (email vererek → &quot;Lead&apos;ler&quot; tab&apos;ı) hem de <strong>giriş yapmış</strong> kullanıcılar (bu tab) tarafından kullanılabiliyor.
                      Şu ana kadar giriş yaparak araç çalıştıran <strong>hiç gerçek kullanıcı olmadı</strong>. Anonim lead&apos;leri &quot;Lead&apos;ler&quot; tab&apos;ından takip edin.
                    </p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">Kullanıcı</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">Araç</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">Başlık</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">Tarih</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {toolRuns.results.map((r) => (
                        <TableRow key={r.id} data-testid={`tool-run-row-${r.id}`}>
                          <TableCell className="font-medium">
                            <div className="text-sm text-[#0F1B36]">{r.user_name || '-'}</div>
                            <a href={`mailto:${r.user_email}`} className="text-xs text-[#52525B] hover:underline">{r.user_email}</a>
                          </TableCell>
                          <TableCell><span className="text-xs uppercase bg-[#3CD9B8]/10 text-[#0F1B36] px-2 py-1">{r.tool_slug}</span></TableCell>
                          <TableCell className="text-sm text-[#0F1B36]">{r.title || '-'}</TableCell>
                          <TableCell className="text-sm text-[#52525B]">{r.created_at ? new Date(r.created_at).toLocaleString('tr-TR') : '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            </TabsContent>

            <TabsContent value="content">
              <SiteContentAdminPanel />
            </TabsContent>
          </Tabs>
        )}
      </main>
    </div>
  );
};

export default AdminPage;
